import React from 'react';
import PropTypes from 'prop-types';
import { format } from 'url';
import config from 'config';
import Tabs from '@mulesoft/anypoint-components/lib/Tabs';
import TabList from '@mulesoft/anypoint-components/lib/TabList';
import Tab from '@mulesoft/anypoint-components/lib/Tab';
import TabPanel from '@mulesoft/anypoint-components/lib/TabPanel';
import PlusIcon from '@mulesoft/anypoint-icons/lib/svg/plus-small.svg';
import RefreshIcon from '@mulesoft/anypoint-icons/lib/svg/refresh-small.svg';
import CloseIcon from '@mulesoft/anypoint-icons/lib/svg/close-small.svg';
import CheckIcon from '@mulesoft/anypoint-icons/lib/svg/check-small.svg';
import { getStagingAgentsByType } from '../../../utils/utils';
import TabPanelReview from './TabPanelReview';
import { tabTheme } from '~/components/AgentScanners/types/commonTypes';
import styles from './Review.css';

const Review = ({
  stagingAgents,
  region,
  isReviewMode,
  isStale,
  selectedAgentIds,
  unIgnoredIds,
  onToggleAgent,
  onSelectAll,
  onDeselectAll,
  onRemoveFromSkipped,
  assetTypeLabel = 'Agents',
  assetType = '',
  platformLabel = ''
}) => {
  const [activeTabIndex, setActiveTabIndex] = React.useState(0);

  const STAGING_STATUS_TAB_INDEX = {
    NEW: 0,
    PENDING_UPDATE: 1,
    PENDING_DELETE: 2,
    EXISTING: 3
  };

  const skipIconURL = format({
    ...config.images.uri,
    pathname: 'skip-small.svg'
  });
  const warningIconURL = format({
    ...config.images.uri,
    pathname: 'warning-circle-fill.svg'
  });

  const isBackendIgnored = (agent) =>
    String(agent.ignored).toLowerCase() === 'true';
  const isIgnored = (agent) =>
    isBackendIgnored(agent) && !unIgnoredIds.has(agent.id);
  const filterNonIgnored = (agents) => agents.filter((a) => !isIgnored(a));
  const getByStagingStatus = (status) =>
    filterNonIgnored(
      getStagingAgentsByType(stagingAgents, status, 'stagingStatus')
    );
  const getByOperation = (op) =>
    filterNonIgnored(getStagingAgentsByType(stagingAgents, op));

  let addAgents;
  let updateAgents;
  let missingAgents;
  let existingAgents;
  let skippedAgents;
  let unsuccessfulAgents;

  const useStagingStatus = isReviewMode || isStale;

  if (useStagingStatus) {
    addAgents = getByStagingStatus('NEW');
    updateAgents = getByStagingStatus('PENDING_UPDATE');
    missingAgents = getByStagingStatus('PENDING_DELETE');
    existingAgents = getByStagingStatus('EXISTING');
    skippedAgents = stagingAgents?.filter(isIgnored) || [];
    unsuccessfulAgents = [];
  } else {
    addAgents = getByOperation('PUBLISHED');
    updateAgents = getByOperation('UPDATED');
    missingAgents = getByOperation('DELETED');
    existingAgents =
      stagingAgents?.filter(
        (a) => a.operationPerformed === 'SKIPPED' && !isIgnored(a)
      ) || [];
    skippedAgents = stagingAgents?.filter(isIgnored) || [];
    unsuccessfulAgents = [
      ...getByOperation('FAILED'),
      ...getByOperation('PENDING')
    ];
  }

  return (
    <div
      className={styles.detailSection}
      data-test-id={
        isReviewMode ? 'scan-review-section' : 'scan-audit-log-section'
      }
    >
      <div className={styles.reviewHeader}>
        <h2 className={styles.reviewTitle}>
          {isReviewMode
            ? `Scan Review (${stagingAgents?.length ?? 0})`
            : 'Scan Audit Log'}
        </h2>
      </div>

      <Tabs
        selectedIndex={activeTabIndex}
        onChange={({ value }) => setActiveTabIndex(value)}
      >
        <TabList>
          <Tab testId="test-tab-add" theme={tabTheme}>
            <span className={`${styles.tabIcon} ${styles.tabIconAdd}`}>
              <PlusIcon />
            </span>
            {isReviewMode ? 'Add' : 'Added'} ({addAgents.length})
          </Tab>
          <Tab testId="test-tab-update" theme={tabTheme}>
            <span className={`${styles.tabIcon} ${styles.tabIconUpdate}`}>
              <RefreshIcon />
            </span>
            {isReviewMode ? 'Update' : 'Updated'} ({updateAgents.length})
          </Tab>
          <Tab testId="test-tab-missing" theme={tabTheme}>
            <span className={`${styles.tabIcon} ${styles.tabIconMissing}`}>
              <CloseIcon />
            </span>
            Missing ({missingAgents.length})
          </Tab>
          <Tab testId="test-tab-existing" theme={tabTheme}>
            <span className={`${styles.tabIcon} ${styles.tabIconExisting}`}>
              <CheckIcon />
            </span>
            Existing ({existingAgents.length})
          </Tab>
          <Tab testId="test-tab-skipped" theme={tabTheme}>
            <span className={`${styles.tabIcon} ${styles.tabIconSkipped}`}>
              <img src={skipIconURL} aria-hidden="true" alt="" />
            </span>
            Skipped ({skippedAgents.length})
          </Tab>
          <Tab
            testId="test-tab-unsuccessful"
            theme={tabTheme}
            display-if={!useStagingStatus}
          >
            <span className={`${styles.tabIcon} ${styles.tabIconUnsuccessful}`}>
              <img src={warningIconURL} aria-hidden="true" alt="" />
            </span>
            Unsuccessful ({unsuccessfulAgents?.length})
          </Tab>
        </TabList>

        {/* Add / Added Panel */}
        <TabPanel>
          <TabPanelReview
            title={
              isReviewMode
                ? `${assetTypeLabel} To Add (${addAgents.length})`
                : 'Imported'
            }
            subtitle={
              isReviewMode
                ? `These ${assetTypeLabel.toLowerCase()} were found in the external system but don't exist in your registry yet.`
                : `These ${assetTypeLabel} were found in the external system but didn't exist in Exchange.`
            }
            typeStyle={styles.reviewImported}
            agents={addAgents}
            region={region}
            assetType={assetType}
            platformLabel={platformLabel}
            typeMsg={isReviewMode ? 'New service' : undefined}
            typeMsgStyle={isReviewMode ? styles.published : undefined}
            showCheckboxes={isReviewMode}
            selectedAgentIds={isReviewMode ? selectedAgentIds : undefined}
            onToggleAgent={isReviewMode ? onToggleAgent : undefined}
            onSelectAll={isReviewMode ? onSelectAll : undefined}
            onDeselectAll={isReviewMode ? onDeselectAll : undefined}
            disableLink={useStagingStatus}
          />
        </TabPanel>

        {/* Update / Updated Panel */}
        <TabPanel>
          <TabPanelReview
            title={
              isReviewMode
                ? `${assetTypeLabel} To Update (${updateAgents.length})`
                : `Update (${updateAgents.length})`
            }
            subtitle={
              isReviewMode
                ? `These ${assetTypeLabel.toLowerCase()} exist in your Exchange but have newer versions available.`
                : `These ${assetTypeLabel} have been updated in your Exchange.`
            }
            typeStyle={styles.reviewUpdated}
            agents={updateAgents}
            region={region}
            assetType={assetType}
            platformLabel={platformLabel}
            typeMsg={isReviewMode ? 'Update available' : undefined}
            typeMsgStyle={isReviewMode ? styles.updated : undefined}
            showCheckboxes={isReviewMode}
            selectedAgentIds={isReviewMode ? selectedAgentIds : undefined}
            onToggleAgent={isReviewMode ? onToggleAgent : undefined}
            onSelectAll={isReviewMode ? onSelectAll : undefined}
            onDeselectAll={isReviewMode ? onDeselectAll : undefined}
            disableLink={useStagingStatus}
          />
        </TabPanel>

        {/* Missing / Removed Panel */}
        <TabPanel>
          <TabPanelReview
            title={`Missing ${assetTypeLabel} (${missingAgents.length})`}
            subtitle={
              isReviewMode
                ? `These ${assetTypeLabel.toLowerCase()} are missing from the source system. Finalizing this scan permanently deletes them from Exchange.`
                : `These ${assetTypeLabel} exist in your Exchange but were not found in the source system.`
            }
            typeStyle={styles.reviewRemove}
            agents={missingAgents}
            region={region}
            assetType={assetType}
            platformLabel={platformLabel}
            typeMsg={isReviewMode ? 'Missing from source' : undefined}
            typeMsgStyle={isReviewMode ? styles.deleted : undefined}
            disableLink={useStagingStatus}
          />
        </TabPanel>

        {/* Existing / Already in Exchange Panel */}
        <TabPanel>
          <TabPanelReview
            title={`Existing ${assetTypeLabel} (${existingAgents.length})`}
            subtitle={
              isReviewMode
                ? `These ${assetTypeLabel.toLowerCase()} are already up to date in your Exchange.`
                : `These ${assetTypeLabel} are up to date in your Exchange.`
            }
            typeStyle={styles.reviewCurrent}
            agents={existingAgents}
            region={region}
            assetType={assetType}
            platformLabel={platformLabel}
            typeMsg={isReviewMode ? 'Up to date' : undefined}
            typeMsgStyle={isReviewMode ? styles.review : undefined}
            disableLink={false}
          />
        </TabPanel>

        {/* Skipped Panel */}
        <TabPanel>
          <TabPanelReview
            title={`Skipped ${assetTypeLabel} (${skippedAgents.length})`}
            subtitle={`These ${assetTypeLabel.toLowerCase()} were previously skipped and will remain excluded from all future scans. To include in future scans, restore them via the overflow menu.`}
            typeStyle={styles.reviewSkipped}
            agents={skippedAgents}
            region={region}
            assetType={assetType}
            platformLabel={platformLabel}
            typeMsg={isReviewMode ? 'Skipped' : undefined}
            typeMsgStyle={isReviewMode ? styles.unsuccessful : undefined}
            disableLink
            onRemoveFromSkipped={
              isReviewMode
                ? (agent) => {
                    onRemoveFromSkipped(agent);
                    const nextTab =
                      STAGING_STATUS_TAB_INDEX[agent.stagingStatus];

                    if (nextTab !== undefined) setActiveTabIndex(nextTab);
                  }
                : undefined
            }
          />
        </TabPanel>

        {/* Unsuccessful Panel (completed audit mode only) */}
        {!useStagingStatus && unsuccessfulAgents?.length > 0 && (
          <TabPanel>
            <TabPanelReview
              title="Unsuccessful"
              subtitle={`We couldn't import these ${assetTypeLabel} We'll try again in the next scan.`}
              typeStyle={styles.reviewUnsuccessful}
              agents={unsuccessfulAgents}
              region={region}
              assetType={assetType}
              platformLabel={platformLabel}
              typeMsg="Unsuccessful"
              typeMsgStyle={styles.unsuccessful}
              displayWarning
              disableLink
            />
          </TabPanel>
        )}
      </Tabs>
    </div>
  );
};

Review.propTypes = {
  stagingAgents: PropTypes.array,
  region: PropTypes.string,
  isReviewMode: PropTypes.bool,
  isStale: PropTypes.bool,
  selectedAgentIds: PropTypes.instanceOf(Set),
  unIgnoredIds: PropTypes.instanceOf(Set),
  onToggleAgent: PropTypes.func,
  onSelectAll: PropTypes.func,
  onDeselectAll: PropTypes.func,
  onRemoveFromSkipped: PropTypes.func,
  assetTypeLabel: PropTypes.string,
  assetType: PropTypes.string,
  platformLabel: PropTypes.string
};

Review.defaultProps = {
  isReviewMode: false,
  isStale: false,
  stagingAgents: [],
  selectedAgentIds: new Set(),
  unIgnoredIds: new Set(),
  assetTypeLabel: PropTypes.string
};

Review.defaultProps = {
  isReviewMode: false,
  isStale: false,
  stagingAgents: [],
  selectedAgentIds: new Set(),
  unIgnoredIds: new Set()
};

export default Review;
